<template>
  <div class="auth-wrapper auth-v2">
    <div class="auth-inner">
      <!-- brand logo -->
      <router-link
        to="/"
        class="brand-logo d-flex align-center"
      >
        <v-img
          :src="appLogo"
          max-height="30px"
          max-width="30px"
          alt="logo"
          contain
          class="me-3 "
        ></v-img>

        <h2 class="text--primary">
          {{ appName }}
        </h2>
      </router-link>
      <!--/ brand logo -->

      <v-row class="auth-row ma-0">
        <v-col
          lg="8"
          class="d-none d-lg-block position-relative overflow-hidden pa-0"
        >
          <div class="auth-illustrator-wrapper">
            <!-- triangle bg -->
            <img
              height="362"
              class="auth-mask-bg"
              :src="require(`@/assets/images/misc/mask-v2-${$vuetify.theme.dark ? 'dark':'light'}.png`)"
            />

            <!-- tree -->
            <v-img
              height="226"
              width="300"
              class="auth-tree"
              src="@/assets/images/misc/tree-4.png"
            ></v-img>

            <!-- 3d character -->
            <div class="d-flex align-center h-full pa-16 pe-0">
              <v-img
                contain
                max-width="100%"
                height="692"
                class="auth-3d-group"
                :src="require(`@/assets/images/3d-characters/group-${$vuetify.theme.dark ? 'dark' : 'light'}.png`)"
              ></v-img>
            </div>
          </div>
        </v-col>

        <v-col
          lg="4"
          class="d-flex align-center auth-bg pa-10 pb-0"
        >
          <v-row>
            <v-col
              cols="12"
              sm="8"
              md="6"
              lg="12"
              class="mx-auto"
            >
              <v-card flat>
                <v-card-text>
                  <p class="mb-2">
                    Please sign-in to your account
                  </p>
                </v-card-text>
                <v-card-text v-if="authError">
                  <v-alert
                    dense
                    text
                    color="error"
                    class="mb-0"
                    data-testid="alert-bad-user"
                  >
                    Bad Email or password
                  </v-alert>
                </v-card-text>

                <!-- login form -->
                <v-card-text>
                  <v-form
                    ref="loginForm"
                    @submit.prevent="handleFormSubmit"
                  >
                    <v-text-field
                      v-model="email"
                      data-testid="input-email"
                      outlined
                      label="Email"
                      placeholder="email"
                      :error-messages="errorMessages.email"
                      :rules="[validators.required, validators.emailValidator]"
                      hide-details="auto"
                      class="mb-6"
                    ></v-text-field>

                    <v-text-field
                      v-model="password"
                      data-testid="input-password"
                      outlined
                      :type="isPasswordVisible ? 'text' : 'password'"
                      label="Password"
                      :error-messages="errorMessages.password"
                      placeholder="Password"
                      :append-icon="isPasswordVisible ? icons.mdiEyeOffOutline:icons.mdiEyeOutline"
                      :rules="[validators.required]"
                      hide-details="auto"
                      class="mb-2"
                      @click:append="isPasswordVisible = !isPasswordVisible"
                    ></v-text-field>

                    <v-btn
                      block
                      data-testid="btn-login"
                      color="secondary"
                      type="submit"
                      class="mt-6"
                      :loading="loginLoading"
                    >
                      Login
                    </v-btn>
                  </v-form>
                </v-card-text>
              </v-card>
              <div
                v-if="!$appConfig.isProduction"
                class="text-center"
              >
                <code>
                  node_env: {{ $nodeEnv }}<br />
                  over_env: {{ $nodeOverEnv }}
                </code>
              </div>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
// eslint-disable-next-line object-curly-newline
import { useNamespacedActions } from 'vuex-composition-helpers';

import { mdiEyeOutline, mdiEyeOffOutline } from '@mdi/js';
import { ref, getCurrentInstance } from '@vue/composition-api';
import { required, emailValidator } from '@core/utils/validation';
import { useRouter } from '@core/utils';
import themeConfig from '@themeConfig';
import { clientApiManagerShared } from '@/api/axios-api';
import { fetchCurrentUser } from '@/services/user/user.services';
import { authTokenRequest } from '@/services/user/auth.services';

export default {
  setup() {
    // Template Ref
    const loginForm = ref(null);
    const { SET_CURRENT_USER } = useNamespacedActions(
      'userStore',
      [
        'SET_CURRENT_USER',
      ],
    );

    const vm = getCurrentInstance().proxy;
    const { router } = useRouter();

    const isPasswordVisible = ref(false);

    const NodeEnvIsDevelopment = process.env.NODE_ENV === 'development';
    const OverEnvIsTest = process.env.VUE_APP_OVER_ENV === 'test';
    const needToFillInputs = NodeEnvIsDevelopment && !OverEnvIsTest;

    const defaultEmail = needToFillInputs ? 'gajjartejas26+man@gmail.com' : '';
    const defaultPwd = needToFillInputs ? 'tss2020' : '';

    const email = ref(defaultEmail);
    const password = ref(defaultPwd);
    const errorMessages = ref([]);
    const authError = ref(false);
    const loginLoading = ref(false);

    const handleFormSubmit = () => {
      const isFormValid = loginForm.value.validate();

      if (!isFormValid) return;

      loginLoading.value = true;
      authTokenRequest({ email: email.value, password: password.value }, clientApiManagerShared)
        .then((data) => {
          // eslint-disable-next-line camelcase
          const { access_token, refresh_token } = data;
          localStorage.setItem('accessToken', access_token);
          localStorage.setItem('refreshToken', refresh_token);

          return data;
        })
        .then(() => {
          fetchCurrentUser().then((userData) => {
            const userAbility = [
              {
                action: 'manage',
                subject: 'all',
              },
            ];
            vm.$ability.update(userAbility);
            localStorage.setItem('userAbility', JSON.stringify(userAbility));
            SET_CURRENT_USER({ ...userData, role: 'admin' });
            localStorage.setItem('userData', JSON.stringify({ ...userData, role: 'admin' }));
            router.push('/');
          });
        })
        .catch((error) => {
          // TODO: Next Update - Show notification
          console.error('Oops, Unable to login!');
          console.log('error :>> ', error.response);
          authError.value = true;
        })
        .finally(() => {
          loginLoading.value = false;
        });
    };

    return {
      handleFormSubmit,
      isPasswordVisible,
      email,
      password,
      errorMessages,
      authError,
      icons: {
        mdiEyeOutline,
        mdiEyeOffOutline,
      },
      validators: {
        required,
        emailValidator,
      },

      // themeConfig
      appName: themeConfig.app.name,
      appLogo: themeConfig.app.logo,
      loginLoading,

      // Template Refs
      loginForm,
    };
  },
};
</script>

<style lang="scss" scoped>
@import '@core/preset/preset/pages/auth.scss';
</style>
